import type { RiskRecord } from '../../../request/risks/risks.types.ts';

/** Coerce any unknown-shaped value into a safe display string. */
export const asText = (value: unknown): string => (value == null ? '' : String(value));

const isUuidLike = (value: unknown): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    asText(value).trim()
  );

/**
 * Navbar subtitles and other **unlabeled** hints: prefer `client_risk_id`, else system `risk_id` / UUID.
 * Do not use for UI labeled “Reference ID” — those stay client reference only (see risks table column).
 * Prefer human display codes (RSK_…) over UUID-shaped values from identify-risks (`riskId` = DB id).
 */
export const resolveDisplayOnlyRiskIdentifier = (
  risk: RiskRecord | null | undefined
): string => {
  const clientRef = asText(risk?.clientRiskId ?? risk?.client_risk_id).trim();
  if (clientRef) return clientRef;
  const snake = asText(risk?.risk_id).trim();
  if (snake && !isUuidLike(snake)) return snake;
  const camel = asText(risk?.riskId).trim();
  if (camel && !isUuidLike(camel)) return camel;
  // Fall back to UUID only when no display code exists (brand-new risk before reload).
  return asText(risk?.risk_id ?? risk?.riskId ?? risk?.id);
};

/** System Risk ID for the meta strip, detail page, and detail links (RSK_… only; never DB UUID). */
export const resolveMetaBarSystemRiskId = (
  risk: RiskRecord | null | undefined
): string => {
  const snake = asText(risk?.risk_id).trim();
  if (snake && !isUuidLike(snake)) return snake;
  const camel = asText(risk?.riskId).trim();
  if (camel && !isUuidLike(camel)) return camel;
  return '';
};

const AUDIT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

/** Format an ISO timestamp into a short, locale-aware "Mon DD, YYYY" string. */
export const formatAuditTimestamp = (timestamp: string | undefined | null): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return String(timestamp);
  return date.toLocaleDateString('en-US', AUDIT_DATE_FORMAT);
};

/** Normalize source enum-like values to readable labels for UI badges. */
export const formatRiskSourceLabel = (source: unknown): string => {
  const raw = asText(source).trim().toLowerCase();
  if (!raw) return '';

  if (raw === 'manual') return 'Manual';
  if (raw === 'ai_assisted') return 'AI Assisted';
  if (raw === 'batch_import') return 'Batch Import';

  return raw
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

/** Risk association rows: cap visible length; full string stays in `title` / tooltips. */
export const truncateProcessDisplayName = (text: unknown, maxLength = 40): string => {
  const s = asText(text);
  if (s.length <= maxLength) return s;
  return `${s.slice(0, maxLength)}…`;
};

/** Tailwind classes for multi-line ellipsis in constrained containers. */
export const LINE_CLAMP_2_CLASS =
  'line-clamp-2 min-w-0 w-full break-words text-left leading-snug';
export const LINE_CLAMP_3_CLASS =
  'line-clamp-3 min-w-0 w-full break-words text-left leading-snug';
